import axios from '@axios'
import router from '@/router'
import { checkGenderForProgram, checkProgramRegistrationIsDisableByGender } from '@/helpers/checkGenderForProgram'
import { hasValue } from '@/helpers/formConditions'

export default {
  namespaced: true,
  state: {
    childrenList: [],
    selectedProgramGender: null,
    selectedProgramCheckGender: false,
    selectedProgramAgeRanges: {
      age_range_from: null,
      age_range_to: null,
    },
    selectedProgramDynamicRequirements: [],
    selectedProgramRegistrationDependencyPrograms: {
      require_previous_programs: false,
    },
    isLoaded: false,
  },
  getters: {
    getChildrenList: state => state.childrenList,
    hasChildrenLoaded: state => state.isLoaded,
  },
  mutations: {
    SET_ALL_CHILDREN: (state, data) => {
      state.childrenList = data
      state.isLoaded = true
    },
    INVALIDATE_CHILDREN_LIST: (state) => {
      state.isLoaded = false
    },
    SET_SELECTED_PROGRAM_AVAILABILITY_SETTINGS: (state, data) => {
      state.selectedProgramGender = checkGenderForProgram(data.gender)
      if (data.age_range_from || data.age_range_to) {
        state.selectedProgramCheckGender = true
        state.selectedProgramAgeRanges = {
          age_range_from: data.age_range_from,
          age_range_to: data.age_range_to,
        }
      } else {
        state.selectedProgramCheckGender = false
        state.selectedProgramAgeRanges = {
          age_range_from: null,
          age_range_to: null,
        }
      }

      state.selectedProgramDynamicRequirements = data.dynamic_requirements
      state.selectedProgramRegistrationDependencyPrograms = data.registration_dependency_programs
    },
  },
  actions: {
    fetchChildrenList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/my-children', {
            params: queryParams,
          })
          .then(response => {
            ctx.commit('SET_ALL_CHILDREN', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    isProgramDisableByGender({ state }, child) {
      const gender = child.extra_user_info ? child.extra_user_info.gender : ''

      return checkProgramRegistrationIsDisableByGender(gender, state.selectedProgramGender)
    },
    async updateChildrenListWithProgramAccess({ state, dispatch, rootState }) {
      // Keep a snapshot so we can restore it if something goes wrong
      const originalChildrenList = [...state.childrenList]

      try {
        state.childrenList = await Promise.all(state.childrenList.map(async child => {
          let isDisabled = false
          let disabledReason = ''
          let ageIsEmpty = false
          let dynamicFieldIsEmpty = false
          let emptyDynamicFields = []
          let needsProfileReview = false

          try {
            const isDisabledByGender = await dispatch('isProgramDisableByGender', child)
            if (isDisabledByGender) {
              isDisabled = true
              disabledReason = `The program is available for ${state.selectedProgramGender.toLowerCase()} only`
            }

            // Check by age
            if (!isDisabled && state.selectedProgramCheckGender) {
              const age = child.extra_user_info?.age ?? null
              const birth_date = child.extra_user_info?.birth_date ?? null

              if (!birth_date) {
                isDisabled = false
                ageIsEmpty = true
              } else {
                const { age_range_from, age_range_to } = state.selectedProgramAgeRanges

                if (age_range_from && age_range_to) {
                  if (age < age_range_from || age > age_range_to) {
                    isDisabled = true
                    disabledReason = `The program is suitable for ages ${age_range_from} - ${age_range_to}`
                  }
                } else if (age_range_from && !age_range_to) {
                  if (age < age_range_from) {
                    isDisabled = true
                    disabledReason = `The program is suitable for ages ${age_range_from} and above`
                  }
                } else if (!age_range_from && age_range_to) {
                  if (age > age_range_to) {
                    isDisabled = true
                    disabledReason = `This program is suitable for ages up to ${age_range_to}`
                  }
                }
              }
            }

            let failedRequirementsLabels = []

            if (state.selectedProgramDynamicRequirements.length > 0) {
              state.selectedProgramDynamicRequirements.forEach(requirement => {
                const dynamicField = child.dynamic_profile_fields.find(field => field.original_id == requirement.id)

                if (!dynamicField || dynamicField.value == null) {
                  dynamicFieldIsEmpty = true
                  emptyDynamicFields.push({ ...requirement })
                } else {
                  const result = hasValue(requirement, dynamicField.value)

                  if (!result) {
                    failedRequirementsLabels.push(requirement.label)
                  }
                }
              })

              if (failedRequirementsLabels.length > 0) {
                isDisabled = true
                disabledReason = `This program is unavailable because it does not meet the requirement: ${failedRequirementsLabels.join(', ')}.`
              }
            }

            if (state.selectedProgramRegistrationDependencyPrograms
                && state.selectedProgramRegistrationDependencyPrograms.require_previous_programs) {

              const { allowed, reasons } = await dispatch('canRegisterCamper', child)

              if (!allowed) {
                isDisabled = true
                disabledReason = `This program is unavailable because it does not meet the requirements:\n• ` + reasons.join('\n• ')
              }
            }
          } catch (childError) {
            // If processing a single child fails, return the child without disabling it
            // so the rest of the list is still rendered correctly
            console.error(`Error processing program access for child ${child.id}:`, childError)
          }

          // Check whether the child's profile has been reviewed after the required date.
          // Review settings are now global (school-wide) and come from authSettings.
          const requireReview = rootState.authSettings.authSettings.require_child_profile_review
          if (requireReview) {
            const reviewedAt = child.extra_user_info?.profile_reviewed_at
            const minDate = rootState.authSettings.authSettings.min_child_profile_review_date

            if (!reviewedAt) {
              needsProfileReview = true
            } else if (minDate) {
              needsProfileReview = new Date(reviewedAt) < new Date(minDate)
            }
          }

          return {
            ...child,
            isDisabled,
            disabledReason,
            ageIsEmpty,
            dynamicFieldIsEmpty,
            emptyDynamicFields,
            needsProfileReview,
          }
        }))
      } catch (error) {
        // Restore the original list so the page doesn't get stuck with no children shown
        console.error('Error updating children list with program access:', error)
        state.childrenList = originalChildrenList
        throw error
      }
    },
    canRegisterCamper({ state }, child) {
      const programDependency = state.selectedProgramRegistrationDependencyPrograms
      if (!programDependency.require_previous_programs) {
        return { allowed: true, reasons: [] }
      }

      const reasons = []

      for (const required of programDependency.required_programs) {
        if (!required.selectedTypes.includes('Camper')) continue

        const programId = required.selectedProgramId
        const programName = required.programName
        const camperCondition = required.statusConditionType?.Camper
        const application = child.applicationsByProgram?.[programId]

        const failed = (() => {
          if (!camperCondition) return false

          if (camperCondition === 'any') {
            return !application
          }

          if (camperCondition === 'past_line') {
            return !application || !application.is_accepted
          }

          if (camperCondition === 'specific') {
            const allowedStatuses = (required.selectedStatuses?.Camper || []).map(s => s.name)
            return !application || !allowedStatuses.includes(application.status)
          }

          return false
        })()

        if (failed) {
          reasons.push(`You must be registered in the program "${programName}" before applying for this program.`)
        }
      }

      return {
        allowed: reasons.length === 0,
        reasons,
      }
    },
    goToApplicationPage(ctx, {child, programId}) {
      if (child.applicationsByProgram[programId] === undefined) {
        router.push({
          name: 'application-create',
          params: { id: programId },
          query: { user_id: child.id }
        })
      } else {
        router.push({
          name: 'application-create',
          params: { id: programId },
          query: {
            app_id: child.applicationsByProgram[programId].id,
            user_id: child.id,
          }
        })
      }
    },
  },
}
